import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Radio, AlertTriangle, CheckCircle, X, Wallet, Info, Trash2, RefreshCw,
  Plus, ChevronDown, ChevronUp, Activity, Copy, Eye, EyeOff, Target,
  PlayCircle, PauseCircle, ExternalLink, Bell,
} from 'lucide-react';
import {
  useAccount, useChainId, useSignTypedData,
  usePublicClient, useWalletClient, useSwitchChain,
} from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Spinner, InfoTip } from '../components/ui';
import TradingViewChart from '../components/TradingViewChart';
import useTokenPrice from '../hooks/useTokenPrice';
import {
  prepareDexTvWebhook, commitDexTvWebhook,
  listDexTvWebhooks, getDexTvWebhook, updateDexTvWebhook, deleteDexTvWebhook,
  refillPrepareDexTvWebhook, refillCommitDexTvWebhook, testDexTvWebhook,
} from '../services/api';
import { CHAINS, PROTOCOL_CHAINS, getTokensFor } from '../constants/dexTokens';
import DexCustomTokenInput from '../components/DexCustomTokenInput';

const ERC20_ABI = [
  { name: 'allowance', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
    outputs: [{ type: 'uint256' }] },
  { name: 'approve', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    outputs: [{ type: 'bool' }] },
];
const MAX_UINT256 = 2n ** 256n - 1n;

const POOL_SIZE_OPTIONS = [
  { value: 5,  label: '5 each side',  desc: '10 sigs · light usage' },
  { value: 10, label: '10 each side', desc: '20 sigs · most users' },
  { value: 20, label: '20 each side', desc: '40 sigs · high-frequency' },
];

const SYMBOL_TO_CG = {
  WETH: 'ethereum',  ETH: 'ethereum',
  WBTC: 'bitcoin',   BTC: 'bitcoin',  cbBTC: 'bitcoin',  'BTC.b': 'bitcoin',
  WMATIC: 'matic-network', MATIC: 'matic-network', POL: 'matic-network',
  WBNB: 'binancecoin', BNB: 'binancecoin',
  WAVAX: 'avalanche-2', AVAX: 'avalanche-2',
  LINK: 'chainlink', UNI: 'uniswap', AAVE: 'aave',
  ARB: 'arbitrum', OP: 'optimism', SOL: 'solana',
  CRV: 'curve-dao-token', MKR: 'maker', COMP: 'compound-governance-token',
};

const API_BASE = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');

function toBaseUnits(humanAmount, decimals) {
  const s = String(humanAmount).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error('amount must be a non-negative decimal');
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  const combined = (whole + padded).replace(/^0+/, '') || '0';
  if (combined === '0') throw new Error('amount must be > 0');
  return combined;
}
function formatBaseUnits(baseStr, decimals, maxFrac = 4) {
  if (!baseStr || baseStr === '0') return '0';
  const s = String(baseStr).padStart(decimals + 1, '0');
  const whole = s.slice(0, -decimals).replace(/^0+/, '') || '0';
  const frac  = s.slice(-decimals).slice(0, maxFrac).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}
function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function copyToClipboard(text) { try { navigator.clipboard?.writeText(text); } catch {} }

const STABLE_SYMBOLS = new Set(['USDC', 'USDT', 'DAI', 'BUSD', 'TUSD', 'FDUSD', 'USDC.e', 'USDbC', 'USDS', 'GUSD', 'LUSD']);
const WRAPPED_PREFIX_RE = /^W(?=ETH|BTC|MATIC|BNB|AVAX|SOL|FTM|CRO|XTZ)/;
function unwrapSymbol(s) {
  if (!s) return '';
  if (s === 'cbBTC' || s === 'BTC.b') return 'BTC';
  if (s === 'POL') return 'MATIC';
  return s.replace(WRAPPED_PREFIX_RE, '');
}
function chartPairFor(sellSym, buySym) {
  const s = unwrapSymbol(sellSym);
  const b = unwrapSymbol(buySym);
  if (!s || !b) return 'BTC/USDT';
  if (STABLE_SYMBOLS.has(s) && !STABLE_SYMBOLS.has(b)) return `${b}/${s}`;
  return `${s}/${b}`;
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium max-w-md
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}
function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

// ─── Secret reveal modal — one-time view after commit ───────────────────────
function SecretRevealModal({ webhookKey, webhookSecret, onClose }) {
  const [secretVisible, setSecretVisible] = useState(false);
  const fullUrl = `${API_BASE}/dex/tv/${webhookKey}`;
  const sampleMessage = JSON.stringify({ action: 'buy', secret: webhookSecret }, null, 2);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-[modal-in_0.2s_ease-out]">
      <style>{`@keyframes modal-in { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }`}</style>
      <div className="bg-dark-800 border border-accent-primary/30 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl shadow-accent-primary/10">
        <div className="px-6 py-4 border-b border-dark-600 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary/30 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/30">
              <Radio className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-white font-bold">Webhook armed — copy these now</h2>
              <p className="text-amber-300 text-xs">The secret is shown once. After this dialog, only the masked version is visible.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-dark-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Webhook URL */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5 flex items-center gap-1">
              <Target className="w-3 h-3" /> Webhook URL <span className="text-gray-600 normal-case">(paste into TradingView's "Webhook URL")</span>
            </label>
            <div className="flex gap-2">
              <code className="flex-1 bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-xs text-white font-mono break-all">{fullUrl}</code>
              <button onClick={() => copyToClipboard(fullUrl)}
                className="px-3 py-2 rounded-lg bg-accent-primary/15 text-accent-primary hover:bg-accent-primary/25 border border-accent-primary/30 text-xs font-semibold flex items-center gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
          </div>

          {/* Secret */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5 flex items-center gap-1">
              <Bell className="w-3 h-3" /> Webhook secret <span className="text-gray-600 normal-case">(used to HMAC-sign each alert)</span>
            </label>
            <div className="flex gap-2">
              <code className="flex-1 bg-dark-700 border border-amber-500/30 rounded-lg px-3 py-2 text-xs text-amber-200 font-mono break-all">
                {secretVisible ? webhookSecret : webhookSecret.replace(/./g, '•')}
              </code>
              <button onClick={() => setSecretVisible(v => !v)}
                className="px-3 py-2 rounded-lg bg-dark-700 text-gray-300 hover:bg-dark-600 border border-dark-600 text-xs font-semibold flex items-center gap-1.5">
                {secretVisible ? <><EyeOff className="w-3.5 h-3.5" /> Hide</> : <><Eye className="w-3.5 h-3.5" /> Show</>}
              </button>
              <button onClick={() => copyToClipboard(webhookSecret)}
                className="px-3 py-2 rounded-lg bg-accent-primary/15 text-accent-primary hover:bg-accent-primary/25 border border-accent-primary/30 text-xs font-semibold flex items-center gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copy
              </button>
            </div>
          </div>

          {/* TradingView setup steps */}
          <div className="bg-dark-700/40 rounded-xl px-4 py-3 border border-dark-600">
            <div className="text-[10px] uppercase tracking-wider text-accent-primary font-semibold mb-2">How to wire this into TradingView</div>
            <ol className="text-xs text-gray-300 space-y-1.5 list-decimal list-inside">
              <li>In TradingView, open your alert &rarr; <strong className="text-white">Notifications</strong> tab.</li>
              <li>Tick <strong className="text-white">Webhook URL</strong>, paste the URL above.</li>
              <li>In the <strong className="text-white">Message</strong> field, paste this JSON exactly:</li>
            </ol>
            <pre className="mt-2 bg-dark-900 border border-dark-600 rounded-lg p-3 text-[11px] text-emerald-300 font-mono overflow-x-auto">
              {sampleMessage}
            </pre>
            <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
              Replace <code className="text-amber-300">buy</code> with <code className="text-amber-300">sell</code> for the sell-side alert.
              The secret in the body authenticates the alert. <strong className="text-amber-300">Don't share this message</strong> — anyone with it can fire the webhook.
              Advanced: if your relay (n8n, Pipedream, custom server) signs requests, it can omit the secret from the body and send HMAC-SHA256 in the <code className="text-gray-300">X-Webhook-Signature</code> header instead.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => { copyToClipboard(`URL: ${fullUrl}\nSecret: ${webhookSecret}`); }}
              className="px-4 py-2 rounded-xl bg-dark-700 text-gray-300 hover:bg-dark-600 border border-dark-600 text-xs font-semibold flex items-center gap-1.5">
              <Copy className="w-3.5 h-3.5" /> Copy both
            </button>
            <button onClick={onClose}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-accent-primary to-amber-500 text-dark-900 text-xs font-bold shadow-lg shadow-accent-primary/20">
              I've saved them — close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Pool stats bar (two-side capacity) ─────────────────────────────────────
function TwoSidePoolBar({ pool }) {
  const buys = Array.isArray(pool) ? pool.filter(e => e.action === 'buy').length : 0;
  const sells = Array.isArray(pool) ? pool.filter(e => e.action === 'sell').length : 0;
  const buysLeft = Array.isArray(pool) ? pool.filter(e => e.action === 'buy' && !e.used).length : 0;
  const sellsLeft = Array.isArray(pool) ? pool.filter(e => e.action === 'sell' && !e.used).length : 0;
  const buyPct = buys > 0 ? (buysLeft / buys) * 100 : 0;
  const sellPct = sells > 0 ? (sellsLeft / sells) * 100 : 0;
  const tone = (pct) => pct <= 10 ? 'bg-red-500' : pct <= 30 ? 'bg-amber-400' : 'bg-emerald-500';
  const text = (pct) => pct <= 10 ? 'text-red-400' : pct <= 30 ? 'text-amber-300' : 'text-emerald-300';

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-emerald-400 font-semibold w-7">BUY</span>
        <div className="flex-1 h-1.5 bg-dark-600 rounded-full overflow-hidden min-w-[60px] max-w-[140px]">
          <div className={`h-full ${tone(buyPct)} transition-all duration-500`} style={{ width: `${Math.max(2, buyPct)}%` }} />
        </div>
        <span className={`text-[10px] font-mono font-medium ${text(buyPct)} whitespace-nowrap`}>{buysLeft}/{buys}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-red-400 font-semibold w-7">SELL</span>
        <div className="flex-1 h-1.5 bg-dark-600 rounded-full overflow-hidden min-w-[60px] max-w-[140px]">
          <div className={`h-full ${tone(sellPct)} transition-all duration-500`} style={{ width: `${Math.max(2, sellPct)}%` }} />
        </div>
        <span className={`text-[10px] font-mono font-medium ${text(sellPct)} whitespace-nowrap`}>{sellsLeft}/{sells}</span>
      </div>
    </div>
  );
}

// ─── Webhook row (expandable) ───────────────────────────────────────────────
function WebhookRow({ webhook, onDelete, onToggleActive, onRefill, onTest, busy, testBusyAction }) {
  const [expanded, setExpanded] = useState(false);
  const protoLabel = webhook.protocol === 'cow' ? 'CoW' : 'Velora';
  const url = `${API_BASE}/dex/tv/${webhook.webhookKey}`;
  const cgId = SYMBOL_TO_CG[webhook.pair?.buySymbol] || null;
  const { data: liveData } = useTokenPrice(cgId, 60000);
  const livePrice = liveData?.currentPriceUsd ?? liveData?.market_data?.current_price?.usd ?? null;
  const priceDriftPct = (livePrice && webhook.referencePriceUsd)
    ? ((livePrice - webhook.referencePriceUsd) / webhook.referencePriceUsd) * 100
    : null;
  const isPriceFar = priceDriftPct !== null && Math.abs(priceDriftPct) > (webhook.slippagePct || 10);

  return (
    <div className="bg-dark-700/40 rounded-xl border border-dark-600/40 overflow-hidden hover:border-dark-500/60 transition-colors">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 text-left transition-colors hover:bg-dark-600/20">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Radio className={`w-3.5 h-3.5 ${webhook.active ? 'text-accent-primary' : 'text-gray-500'}`} />
              <span className="text-white text-sm font-semibold truncate">{webhook.name || 'TradingView Webhook'}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${
                webhook.active
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 animate-[grid-pulse_2s_ease-in-out_infinite]'
                  : 'bg-amber-500/10 text-amber-300 border-amber-500/20'
              }`}>
                {webhook.active ? 'Active' : 'Paused'}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">{protoLabel}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">{webhook.pair?.sellSymbol} ↔ {webhook.pair?.buySymbol}</span>
              {isPriceFar && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-semibold">
                  price drifted &gt;{webhook.slippagePct}%
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap mt-2">
              <p className="text-xs">
                <span className="text-gray-500">Per signal:</span>{' '}
                <span className="text-white">{formatBaseUnits(webhook.amountPerSignalBase, webhook.pair?.sellDecimals || 6)} {webhook.pair?.sellSymbol}</span>
              </p>
              <p className="text-xs">
                <span className="text-gray-500">Slippage:</span>{' '}
                <span className="text-white">{webhook.slippagePct}%</span>
              </p>
              <p className="text-xs">
                <span className="text-gray-500">Alerts:</span>{' '}
                <span className={webhook.alertsCount > 0 ? 'text-blue-300 font-semibold' : 'text-gray-300'}>{webhook.alertsCount || 0}</span>
                {' · '}
                <span className="text-gray-500">Fills:</span>{' '}
                <span className={webhook.fillsCount > 0 ? 'text-emerald-400 font-semibold' : 'text-gray-300'}>{webhook.fillsCount || 0}</span>
              </p>
              <TwoSidePoolBar pool={webhook.orderPool} />
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <button onClick={() => onRefill(webhook.webhookKey)} disabled={busy} title="Sign more orders"
              className="p-1.5 rounded-lg bg-dark-700 text-accent-primary hover:bg-accent-primary/10 disabled:opacity-50 transition-colors">
              <Plus className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onToggleActive(webhook.webhookKey, !webhook.active)} disabled={busy}
              title={webhook.active ? 'Pause (rejects alerts)' : 'Resume'}
              className="p-1.5 rounded-lg bg-dark-700 text-amber-400 hover:bg-amber-500/10 disabled:opacity-50 transition-colors">
              {webhook.active ? <PauseCircle className="w-3.5 h-3.5" /> : <PlayCircle className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => onDelete(webhook.webhookKey)} disabled={busy} title="Delete webhook"
              className="p-1.5 rounded-lg bg-dark-700 text-red-400 hover:bg-red-500/10 disabled:opacity-50 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <span className="ml-1 text-gray-500" onClick={() => setExpanded(e => !e)}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 border-t border-dark-600/40 bg-dark-800/40 space-y-3 animate-[tv-fade_0.2s_ease-out]">
          <style>{`@keyframes tv-fade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>

          {/* Test buttons — fire a real alert via in-app JWT auth (no terminal needed) */}
          <div className="bg-gradient-to-r from-accent-primary/5 to-accent-primary/10 rounded-lg px-3 py-2.5 border border-accent-primary/20">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-wider text-accent-primary font-semibold flex items-center gap-1.5">
                <Activity className="w-3 h-3" />
                Manual fire
              </div>
              <span className="text-[10px] text-gray-500">Consumes one pool order, just like a real TradingView fire</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => onTest(webhook.webhookKey, 'buy')}
                disabled={!!busy || !webhook.active}
                className="px-3 py-2 rounded-xl text-xs font-semibold border bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition-all">
                {testBusyAction === 'buy' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                Fire BUY
              </button>
              <button onClick={() => onTest(webhook.webhookKey, 'sell')}
                disabled={!!busy || !webhook.active}
                className="px-3 py-2 rounded-xl text-xs font-semibold border bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 transition-all">
                {testBusyAction === 'sell' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full bg-red-400" />}
                Fire SELL
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
              {webhook.active
                ? 'Each click pops one order from the matching pool, submits it to CoW/Velora, and logs the alert here. Perfect for testing your strategy without TradingView.'
                : 'Webhook is paused. Resume it to enable manual fires.'}
            </p>
          </div>

          {/* Webhook URL (always visible) */}
          <div className="bg-dark-700/40 rounded-lg px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1 flex items-center gap-1">
              <Target className="w-3 h-3" /> Webhook URL
            </div>
            <div className="flex gap-2">
              <code className="flex-1 bg-dark-900 border border-dark-600 rounded px-2 py-1 text-xs text-white font-mono break-all">{url}</code>
              <button onClick={() => copyToClipboard(url)}
                title="Copy URL"
                className="px-2 py-1 rounded bg-dark-700 text-accent-primary hover:bg-accent-primary/10 border border-dark-600 text-xs">
                <Copy className="w-3 h-3" />
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Secret was shown once at creation — if you've lost it, delete and recreate the webhook.</p>
          </div>

          {/* Price snapshot vs live */}
          {cgId && (
            <div className="bg-gradient-to-r from-dark-700/50 to-dark-700/20 rounded-lg px-3 py-2.5 border border-dark-600">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1.5">
                    <Activity className="w-3 h-3 text-emerald-400 animate-pulse" />
                    Live {webhook.pair?.buySymbol}
                  </div>
                  <div className="text-base font-mono font-semibold text-white mt-0.5">
                    {livePrice != null ? `$${Number(livePrice).toFixed(livePrice >= 1 ? 2 : 4)}` : '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Signed at</div>
                  <div className="text-base font-mono font-semibold text-gray-300 mt-0.5">
                    ${Number(webhook.referencePriceUsd).toFixed(webhook.referencePriceUsd >= 1 ? 2 : 4)}
                  </div>
                </div>
              </div>
              {priceDriftPct !== null && (
                <div className="mt-2 pt-2 border-t border-dark-600 text-xs flex items-center justify-between">
                  <span className="text-gray-400">Price drift since signing</span>
                  <span className={isPriceFar ? 'text-amber-300 font-semibold' : 'text-gray-300 font-mono'}>
                    {priceDriftPct > 0 ? '+' : ''}{priceDriftPct.toFixed(2)}%
                  </span>
                </div>
              )}
              {isPriceFar && (
                <p className="text-[10px] text-amber-300 mt-1.5">
                  Price has moved beyond your slippage band — new alerts may fail to fill. Sign more orders at the current price.
                </p>
              )}
            </div>
          )}

          {/* Alerts log */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Alert log (last {webhook.alertsLog?.length || 0})</div>
            {Array.isArray(webhook.alertsLog) && webhook.alertsLog.length > 0 ? (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {webhook.alertsLog.map((a) => {
                  const statusCls = a.status === 'submitted' ? 'text-emerald-400'
                                  : a.status === 'submit_failed' ? 'text-red-400'
                                  : a.status === 'no_orders' ? 'text-amber-300'
                                  : 'text-gray-400';
                  const statusLabel = a.status === 'submitted' ? '✓ submitted'
                                    : a.status === 'submit_failed' ? '✗ failed'
                                    : a.status === 'no_orders' ? '⚠ no orders'
                                    : a.status;
                  return (
                    <div key={a.id} className="flex items-center justify-between text-xs px-3 py-1.5 bg-dark-700/40 rounded-lg">
                      <span className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${a.action === 'buy' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        <span className={a.action === 'buy' ? 'text-emerald-400' : 'text-red-400'}>{a.action?.toUpperCase()}</span>
                        <span className={statusCls}>{statusLabel}</span>
                        {a.source === 'in_app_test' && (
                          <span className="text-[9px] px-1 py-px rounded bg-accent-primary/15 text-accent-primary border border-accent-primary/30 font-semibold uppercase tracking-wider">manual</span>
                        )}
                        {a.orderbookId && (
                          <button onClick={() => copyToClipboard(a.orderbookId)}
                            title="Copy order ID"
                            className="text-gray-500 hover:text-accent-primary font-mono text-[10px]">
                            {String(a.orderbookId).slice(0, 8)}…
                          </button>
                        )}
                      </span>
                      <span className="text-gray-500 text-[10px]">{fmtTime(a.receivedAt)}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-dark-700/30 border border-dashed border-dark-600 rounded-lg px-3 py-4 text-center">
                <p className="text-gray-500 text-xs">No alerts received yet. Fire one manually above, or wait for TradingView.</p>
              </div>
            )}
          </div>

          {/* Webhook metadata */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-dark-700/40 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Created</div>
              <div className="text-xs text-gray-300">{fmtTime(webhook.createdAt)}</div>
            </div>
            <div className="bg-dark-700/40 rounded-lg px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Orders valid until</div>
              <div className="text-xs text-gray-300">{fmtTime(new Date((webhook.ordersValidTo || 0) * 1000).toISOString())}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create panel ───────────────────────────────────────────────────────────
function CreateWebhookPanel({ onCreated, onToast, onSecretRevealed, onPairChange }) {
  const { address: connectedAddr, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const { signTypedDataAsync } = useSignTypedData();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChain, isPending: switchingChain } = useSwitchChain();

  const [name, setName] = useState('');
  const [protocol, setProtocol] = useState('cow');
  const [chainId, setChainId] = useState(connectedChainId || 1);
  const [sellSym, setSellSym] = useState('USDC');
  const [buySym,  setBuySym]  = useState('WETH');
  const [amountPerSignal, setAmountPerSignal] = useState('');
  const [slippagePct, setSlippagePct] = useState(10);
  const [poolSize, setPoolSize] = useState(10);
  const [stage, setStage] = useState(null);
  const [signProgress, setSignProgress] = useState({ done: 0, total: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [customTick, setCustomTick] = useState(0);

  const tokens = useMemo(() => getTokensFor(chainId), [chainId, customTick]);
  const sellToken = tokens.find(t => t.symbol === sellSym);
  const buyToken  = tokens.find(t => t.symbol === buySym);
  const availableChains = CHAINS.filter(c => PROTOCOL_CHAINS[protocol].includes(c.id));
  const cgId = SYMBOL_TO_CG[buySym] || null;
  const { data: liveData } = useTokenPrice(cgId, 30000);
  const livePrice = liveData?.currentPriceUsd ?? liveData?.market_data?.current_price?.usd ?? null;

  useEffect(() => {
    const list = getTokensFor(chainId);
    if (!list.find(t => t.symbol === sellSym)) setSellSym(list[0]?.symbol || '');
    if (!list.find(t => t.symbol === buySym))  setBuySym(list[1]?.symbol || list[0]?.symbol || '');
    if (sellSym && buySym && sellSym === buySym) {
      const alt = list.find(t => t.symbol !== sellSym);
      if (alt) setBuySym(alt.symbol);
    }
  }, [chainId, sellSym, buySym]);

  useEffect(() => {
    if (!PROTOCOL_CHAINS[protocol].includes(chainId)) setChainId(PROTOCOL_CHAINS[protocol][0]);
  }, [protocol, chainId]);

  useEffect(() => {
    if (sellSym && buySym && onPairChange) onPairChange({ sellSym, buySym });
  }, [sellSym, buySym, onPairChange]);

  const disabledReason = (() => {
    if (!isConnected || !connectedAddr) return 'Connect your wallet first';
    if (Number(connectedChainId) !== Number(chainId))
      return `Wallet is on chain ${connectedChainId}, not ${CHAINS.find(c => c.id === Number(chainId))?.label || chainId}`;
    if (!sellToken || !buyToken || sellToken.symbol === buyToken.symbol) return 'Sell and buy tokens must be different';
    if (!(Number(amountPerSignal) > 0)) return 'Enter an amount per signal > 0';
    if (submitting) return 'Submitting…';
    return null;
  })();
  const canSubmit = disabledReason == null;

  async function ensureAllowance(spender, requiredAmount) {
    setStage('checking-allowance');
    async function read() {
      return publicClient.readContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'allowance', args: [connectedAddr, spender],
      });
    }
    let allowance = await read();
    if (allowance >= requiredAmount) return;
    if (allowance > 0n) {
      setStage('approving');
      onToast({ type: 'info', message: `${sellSym} needs allowance reset to 0 first — confirm in wallet.` });
      await walletClient.writeContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'approve', args: [spender, 0n],
      });
      const start = Date.now();
      while (Date.now() - start < 180_000) {
        await new Promise(r => setTimeout(r, 4000));
        try { allowance = await read(); } catch {}
        if (allowance === 0n) break;
      }
      if (allowance !== 0n) throw new Error('Allowance reset did not confirm');
    }
    setStage('approving');
    onToast({ type: 'info', message: `Approve ${sellSym} once. ~15–30s for on-chain confirmation.` });
    await walletClient.writeContract({
      address: sellToken.address, abi: ERC20_ABI,
      functionName: 'approve', args: [spender, MAX_UINT256],
    });
    const start = Date.now();
    while (Date.now() - start < 180_000) {
      await new Promise(r => setTimeout(r, 4000));
      try { allowance = await read(); } catch {}
      if (allowance >= requiredAmount) return;
    }
    throw new Error('Approval did not confirm in 3 min');
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const amountBase = toBaseUnits(amountPerSignal, sellToken.decimals);

      setStage('preparing');
      const prep = await prepareDexTvWebhook({
        protocol, chainId,
        owner: connectedAddr,
        name: name.trim(),
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        amountPerSignalBase: amountBase,
        slippagePct: Number(slippagePct),
        poolSize: Number(poolSize),
      });
      const { webhookKey, webhookSecret, buys, sells, spender, referencePriceUsd, validTo, totalSignaturesRequired } = prep.data;
      // Total allowance needed = (poolSize buys × amount/buy) — only the buys consume the stable allowance;
      // sells use the buyToken's allowance which must be approved separately when the user has buy-side balance.
      // For v1 we just approve the stable for all buys; sell-side approval happens lazily (CoW handles it during fill).
      setSignProgress({ done: 0, total: totalSignaturesRequired });

      const buysTotalSell = buys.reduce((acc, b) => acc + BigInt(b.sellAmount), 0n);
      await ensureAllowance(spender, buysTotalSell);

      const signedBuys = [], signedSells = [];
      let done = 0;
      for (const leg of buys) {
        setStage(`signing-buy-${done + 1}`);
        const sig = await signTypedDataAsync({
          domain: leg.typedData.domain, types: leg.typedData.types,
          primaryType: leg.typedData.primaryType, message: leg.order,
        });
        signedBuys.push({ ...leg, signature: sig });
        done++; setSignProgress({ done, total: totalSignaturesRequired });
      }
      for (const leg of sells) {
        setStage(`signing-sell-${done + 1}`);
        const sig = await signTypedDataAsync({
          domain: leg.typedData.domain, types: leg.typedData.types,
          primaryType: leg.typedData.primaryType, message: leg.order,
        });
        signedSells.push({ ...leg, signature: sig });
        done++; setSignProgress({ done, total: totalSignaturesRequired });
      }

      setStage('committing');
      const result = await commitDexTvWebhook({
        webhookKey, webhookSecret,
        protocol, chainId, owner: connectedAddr,
        name: name.trim(),
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        amountPerSignalBase: amountBase,
        slippagePct: Number(slippagePct),
        poolSize: Number(poolSize),
        referencePriceUsd, validTo,
        buys: signedBuys, sells: signedSells,
      });
      onToast({ type: 'success', message: `TradingView Webhook armed — copy the URL + secret in the popup.` });
      // Reveal secret once
      onSecretRevealed({ webhookKey, webhookSecret });
      onCreated(result.data?.record);
      setName(''); setAmountPerSignal('');
    } catch (err) {
      const msg = err?.shortMessage || err?.message || 'Could not create webhook';
      onToast({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
      setStage(null);
      setSignProgress({ done: 0, total: 0 });
    }
  }

  const stageLabel = (() => {
    if (stage === 'preparing') return 'Snapshotting reference price + building orders…';
    if (stage === 'checking-allowance') return 'Checking allowance…';
    if (stage === 'approving') return `Approving ${sellSym}…`;
    if (stage?.startsWith('signing-')) return `Sign ${signProgress.done + 1} of ${signProgress.total}…`;
    if (stage === 'committing') return 'Saving webhook…';
    return 'Arm Webhook';
  })();

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent-primary/20 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/20">
          <Radio className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">New TradingView Webhook</h3>
          <p className="text-gray-500 text-xs">Signal from any chart → DEX swap. Pre-signed pool, no wallet needed at fire time.</p>
        </div>
      </div>

      {!isConnected && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-amber-300 text-sm font-semibold">Connect your wallet first</p>
          </div>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button onClick={openConnectModal}
                className="w-full py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 flex items-center justify-center gap-2">
                <Wallet className="w-4 h-4" /> Connect Wallet
              </button>
            )}
          </ConnectButton.Custom>
        </div>
      )}
      {isConnected && connectedAddr && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-4 py-2 mb-4 flex items-center gap-2 text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-300">Connected as <code className="font-mono">{connectedAddr.slice(0,6)}…{connectedAddr.slice(-4)}</code></span>
        </div>
      )}
      {isConnected && Number(connectedChainId) !== Number(chainId) && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-blue-300 text-xs">Wallet is on chain {connectedChainId}; switch to {CHAINS.find(c => c.id === chainId)?.label}.</p>
          </div>
          <button onClick={() => switchChain?.({ chainId: Number(chainId) })} disabled={switchingChain || !switchChain}
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-blue-500 hover:bg-blue-400 text-white disabled:opacity-40">
            {switchingChain ? 'Switching…' : `Switch to ${CHAINS.find(c => c.id === Number(chainId))?.label}`}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Name <span className="text-gray-600 normal-case">(optional)</span></label>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. BTC RSI oversold long"
            maxLength={80}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
        </div>

        {/* Protocol */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Protocol</label>
          <div className="grid grid-cols-2 gap-2">
            {['cow', 'velora'].map(p => (
              <button key={p} type="button" onClick={() => setProtocol(p)}
                className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                  protocol === p ? 'bg-accent-primary/10 border-accent-primary/40 text-white shadow-[0_0_0_1px_rgba(245,158,11,0.1)]' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                {p === 'cow' ? 'CoW' : 'Velora'}
              </button>
            ))}
          </div>
        </div>

        {/* Chain */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Network</label>
          <select value={chainId} onChange={e => setChainId(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
            {availableChains.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        {/* Pair */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Stable</label>
            <select value={sellSym} onChange={e => setSellSym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.map(t => <option key={t.address} value={t.symbol}>{t.symbol}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              Per signal
              <InfoTip side="top">How much to swap on each TradingView alert. Total commitment = per-signal × pool size × 2 (both sides). Pick based on max-tolerable-per-alert.</InfoTip>
            </label>
            <input type="number" min="0" step="any" value={amountPerSignal}
              onChange={e => setAmountPerSignal(e.target.value)} placeholder="100"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Trade</label>
            <select value={buySym} onChange={e => setBuySym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.filter(t => t.symbol !== sellSym).map(t => <option key={t.address} value={t.symbol}>{t.symbol}</option>)}
            </select>
          </div>
        </div>

        <DexCustomTokenInput chainId={chainId}
          onResolved={(tok) => { setCustomTick(t => t + 1); setBuySym(tok.symbol); }} />

        {/* Live price ref */}
        {cgId && livePrice && (
          <div className="bg-dark-700/60 border border-dark-600 rounded-xl px-3 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Activity className="w-3 h-3 text-emerald-400 animate-pulse" />
              <span className="text-[11px] text-gray-400">Reference {buySym} price:</span>
              <span className="text-sm text-white font-mono font-semibold">${Number(livePrice).toFixed(livePrice >= 1 ? 2 : 4)}</span>
            </div>
            <span className="text-[10px] text-gray-500">Snapshot taken at sign time</span>
          </div>
        )}

        {/* Pool size */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Pool size <span className="text-gray-600 normal-case">(orders per direction)</span>
            <InfoTip side="right">N each side = 2N total signatures. Each TradingView alert pulls one order from the matching pool. Refill when pool ≤30% remaining.</InfoTip>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {POOL_SIZE_OPTIONS.map(o => (
              <button key={o.value} type="button" onClick={() => setPoolSize(o.value)}
                className={`text-left px-3 py-2 rounded-xl border transition-all ${
                  poolSize === o.value ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                <div className="text-xs font-semibold">{o.label}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">{o.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Slippage */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
            Slippage band <span className="text-gray-600 normal-case">(wider = more fills when price moves)</span>
            <InfoTip side="right">How far the market can move from the snapshot price before the pre-signed order is invalid. Wider = more fills but worse execution. Too tight = alerts fire but orders may not fill.</InfoTip>
          </label>
          <input type="number" min="1" max="25" step="0.5" value={slippagePct}
            onChange={e => setSlippagePct(e.target.value)}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary" />
          <p className="text-[10px] text-gray-500 mt-1">
            Pre-signed orders fill as long as price hasn't moved more than this band from the reference snapshot.
            Orders are valid for 7 days; sign again to refresh the price reference.
          </p>
        </div>

        <button onClick={handleSubmit} disabled={!canSubmit}
          className={`relative w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all overflow-hidden
            ${canSubmit
              ? 'bg-gradient-to-r from-accent-primary to-amber-500 text-dark-900 shadow-lg shadow-accent-primary/20 hover:shadow-xl hover:shadow-accent-primary/30 hover:-translate-y-px'
              : 'bg-dark-700 text-gray-500'}`}>
          {submitting ? <Spinner size="sm" /> : <Radio className="w-4 h-4" />}
          {stageLabel}
        </button>
        {disabledReason ? (
          <p className="text-amber-400 text-[11px] text-center flex items-center justify-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {disabledReason}
          </p>
        ) : (
          <p className="text-gray-500 text-[11px] text-center">
            One approval + {poolSize * 2} signatures total. Webhook URL + secret shown once after arming.
          </p>
        )}
      </div>
    </Card>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function DexTvWebhooksPage() {
  const { signTypedDataAsync } = useSignTypedData();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null);
  const [testBusy, setTestBusy] = useState(null); // { webhookKey, action }
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [chartPair, setChartPair] = useState('ETH/USDC');
  const handlePairChange = useCallback(({ sellSym, buySym }) => {
    setChartPair(chartPairFor(sellSym, buySym));
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try { const r = await listDexTvWebhooks(); setWebhooks(r.data?.webhooks || []); }
    catch (err) { setToast({ type: 'error', message: err?.message || 'Could not load webhooks' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const handleCreated = (record) => { if (record) reload(); };
  const handleSecretRevealed = (info) => setRevealedSecret(info);

  const handleToggleActive = async (key, active) => {
    setBusy(key);
    try {
      await updateDexTvWebhook(key, { active });
      await reload();
      setToast({ type: 'success', message: active ? 'Webhook resumed.' : 'Webhook paused — incoming alerts will be rejected.' });
    } catch (err) { setToast({ type: 'error', message: err?.message || 'Update failed' }); }
    finally { setBusy(null); }
  };

  const handleDelete = async (key) => {
    if (!window.confirm('Delete this webhook? The URL will stop working immediately and any unused pre-signed orders are abandoned (CoW orders left on the orderbook expire on their own validTo).')) return;
    setBusy(key);
    try {
      await deleteDexTvWebhook(key);
      await reload();
      setToast({ type: 'success', message: 'Webhook deleted.' });
    } catch (err) { setToast({ type: 'error', message: err?.message || 'Delete failed' }); }
    finally { setBusy(null); }
  };

  const handleTest = async (key, action) => {
    setTestBusy({ webhookKey: key, action });
    try {
      const r = await testDexTvWebhook(key, { action });
      const data = r.data || {};
      if (data.success) {
        setToast({ type: 'success', message: `${action.toUpperCase()} fired ✓ — order ${String(data.orderbookId || '').slice(0, 10)}… on orderbook. Pool: ${data.poolStats?.totalRemaining}/${data.poolStats?.totalSigned}.` });
      } else if (data.reason === 'no_orders') {
        setToast({ type: 'error', message: `No unused ${action} orders left in the pool. Click + to sign more.` });
      } else if (data.submitError) {
        setToast({ type: 'error', message: `Order consumed but orderbook submit failed: ${data.submitError}` });
      } else {
        setToast({ type: 'error', message: 'Test alert did not fire — see alert log for details.' });
      }
      await reload();
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'Manual fire failed' });
    } finally {
      setTestBusy(null);
    }
  };

  const handleRefill = async (key) => {
    setBusy(key);
    try {
      const prep = await refillPrepareDexTvWebhook(key);
      const { buys, sells } = prep.data;
      if (!buys?.length || !sells?.length) throw new Error('Nothing to sign');
      const signedBuys = [], signedSells = [];
      for (const leg of buys) {
        const sig = await signTypedDataAsync({
          domain: leg.typedData.domain, types: leg.typedData.types,
          primaryType: leg.typedData.primaryType, message: leg.order,
        });
        signedBuys.push({ ...leg, signature: sig });
      }
      for (const leg of sells) {
        const sig = await signTypedDataAsync({
          domain: leg.typedData.domain, types: leg.typedData.types,
          primaryType: leg.typedData.primaryType, message: leg.order,
        });
        signedSells.push({ ...leg, signature: sig });
      }
      const r = await refillCommitDexTvWebhook(key, {
        buys: signedBuys, sells: signedSells,
        referencePriceUsd: prep.data.referencePriceUsd,
        validTo: prep.data.validTo,
      });
      await reload();
      setToast({ type: 'success', message: `Added ${r.data?.added || 0} orders to webhook pool.` });
    } catch (err) { setToast({ type: 'error', message: err?.message || 'Refill failed' }); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <style>{`
        @keyframes grid-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.65; } }
      `}</style>
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary/25 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/20">
            <Radio className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">DEX TradingView Webhooks</h1>
            <p className="text-gray-400 text-sm">Signal from any chart → autonomous DEX swap on CoW/Velora. Pre-signed pool means alerts fire without your wallet open.</p>
          </div>
        </div>
      </div>

      <Card className="!p-3">
        <div className="flex items-center justify-between mb-2 px-2 pt-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-xs uppercase tracking-wide">Reference chart</span>
            <span className="text-white text-sm font-mono">{chartPair}</span>
          </div>
          <span className="text-[10px] text-gray-500">Powered by TradingView</span>
        </div>
        <TradingViewChart symbol={chartPair} interval="60" height={320} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreateWebhookPanel
          onCreated={handleCreated}
          onToast={setToast}
          onSecretRevealed={handleSecretRevealed}
          onPairChange={handlePairChange}
        />
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm">Your webhooks</h3>
              <p className="text-gray-500 text-xs">{webhooks.length} configured</p>
            </div>
            <button onClick={reload} disabled={loading}
              className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-10">
              <Radio className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No webhooks yet. Create one and paste its URL into any TradingView alert.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {webhooks.map(w => (
                <WebhookRow key={w.webhookKey} webhook={w}
                  onDelete={handleDelete} onToggleActive={handleToggleActive}
                  onRefill={handleRefill} onTest={handleTest}
                  busy={busy === w.webhookKey}
                  testBusyAction={testBusy?.webhookKey === w.webhookKey ? testBusy.action : null} />
              ))}
            </div>
          )}
        </Card>
      </div>

      {revealedSecret && (
        <SecretRevealModal
          webhookKey={revealedSecret.webhookKey}
          webhookSecret={revealedSecret.webhookSecret}
          onClose={() => setRevealedSecret(null)}
        />
      )}
      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
